import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowDown, ChevronLeft, ChevronRight, ShieldCheck, Phone } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { CountUp } from "../CountUp";
import { scrollToY } from "../ScrollControls";

const SLIDES = ["/assets/hero-1.jpg", "/assets/hero-2.jpg", "/assets/hero-3.jpg"];
const DURATION = 6500;

const STATS = [
  { to: 25, suffix: "+", key: "years" },
  { to: 500, suffix: "+", key: "companies" },
  { to: 50, suffix: "K+", key: "recruitments" },
  { to: 12, suffix: "+", key: "industries" },
];

export const Words = ({ text, className = "", delay = 0, step = 0.08 }) => (
  <span className={`inline-block ${className}`}>
    {text.split(" ").map((w, i) => (
      <span key={i} className="inline-block overflow-hidden align-bottom pb-[0.12em] -mb-[0.12em]">
        <motion.span
          className="inline-block"
          initial={{ y: "110%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "-110%", opacity: 0, transition: { duration: 0.45, delay: i * 0.03, ease: [0.22, 1, 0.36, 1] } }}
          transition={{ duration: 0.8, delay: delay + i * step, ease: [0.22, 1, 0.36, 1] }}
        >
          {w}&nbsp;
        </motion.span>
      </span>
    ))}
  </span>
);

export default function Hero() {
  const { t, isRtl } = useLang();
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const lines = [t("hero.l1"), t("hero.l2"), t("hero.l3")];

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), DURATION);
    return () => clearInterval(id);
  }, [idx]);

  const go = (n) => setIdx((i) => (i + n + SLIDES.length) % SLIDES.length);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#1B2A4E]" data-testid="hero-section">
      <AnimatePresence mode="sync">
        <motion.div
          key={idx}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.2 }, scale: { duration: 1.6, ease: [0.22, 1, 0.36, 1] } }}
        >
          <motion.img
            src={SLIDES[idx]}
            alt="Hire Host"
            className="w-full h-full object-cover object-top"
            initial={{ scale: 1 }}
            animate={{ scale: 1.14 }}
            transition={{ duration: DURATION / 1000 + 2, ease: "linear" }}
          />
        </motion.div>
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-r rtl:bg-gradient-to-l from-[#1B2A4E]/95 via-[#1B2A4E]/70 to-[#1B2A4E]/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A4E] via-transparent to-[#1B2A4E]/40" />
      <div className="absolute inset-0 dot-grid opacity-30 mix-blend-overlay" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 w-full pt-40 pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-md rounded-full px-4 py-2 mb-8"
          data-testid="hero-badge"
        >
          <ShieldCheck className="w-4 h-4 text-[#F5A623]" />
          <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-white/85">{t("hero.overline")}</span>
        </motion.div>

        <div className="max-w-4xl">
          <AnimatePresence mode="wait">
            <h1 key={idx} className={`font-head font-black text-4xl sm:text-6xl xl:text-[5.5rem] text-white min-h-[2.2em] ${isRtl ? "leading-[1.35]" : "leading-[1.02]"} tracking-tight`} data-testid="hero-headline">
              <Words text={lines[idx]} />
              <br />
              <Words text={lines[(idx + 1) % 3]} className="text-[#F5A623]" delay={0.25} />
            </h1>
          </AnimatePresence>
        </div>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 mt-10 items-end">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7 }}
              className="max-w-xl text-base sm:text-lg text-white/75 leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t("hero.sub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <button data-testid="hero-cta-employer" onClick={() => navigate("/employer#inquiry")} className="btn-primary pulse-glow group">
                {t("hero.cta1")} <ArrowUpRight className={`w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${isRtl ? "-scale-x-100" : ""}`} />
              </button>
              <button data-testid="hero-cta-jobs" onClick={() => navigate("/employee")} className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white font-bold px-6 py-3.5 text-sm hover:bg-white hover:text-[#1B2A4E] transition-all duration-300">
                {t("hero.cta2")}
              </button>
              <a href="tel:+919867288688" dir="ltr" className="inline-flex items-center gap-2 text-white/80 text-sm font-bold hover:text-[#F5A623] transition-colors ms-2" data-testid="hero-phone">
                <span className="w-9 h-9 rounded-full border border-white/30 flex items-center justify-center"><Phone className="w-4 h-4" /></span>
                +91 98672 88688
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: isRtl ? -40 : 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/15 rounded-2xl overflow-hidden border border-white/15 backdrop-blur-xl"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.key} className="bg-[#1B2A4E]/50 px-4 py-5">
                <div className="font-head font-black text-2xl sm:text-3xl text-[#F5A623]" dir="ltr">
                  <CountUp to={s.to} suffix={s.suffix} />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1 font-bold">{t(`stats.${s.key}`)}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <button
        data-testid="hero-scroll-explore"
        onClick={() => scrollToY(window.innerHeight)}
        className="absolute bottom-8 start-6 sm:start-12 flex items-center gap-3 text-white/70 hover:text-white text-[11px] font-bold tracking-[0.25em] uppercase transition-colors"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center">
          <ArrowDown className="w-4 h-4" />
        </motion.span>
        {t("hero.scroll")}
      </button>

      <div className="absolute bottom-8 end-6 sm:end-12 flex items-center gap-5" data-testid="hero-slider-nav">
        <span className="font-head font-black text-white text-sm tracking-widest" dir="ltr">
          <span className="text-[#F5A623] text-2xl">0{idx + 1}</span> / 0{SLIDES.length}
        </span>
        <div className="flex gap-2">
          <button aria-label="Previous" data-testid="hero-prev" onClick={() => go(-1)} className="w-11 h-11 rounded-full border border-white/30 text-white flex items-center justify-center hover:bg-[#E8842C] hover:border-[#E8842C] transition-colors">
            {isRtl ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
          <button aria-label="Next" data-testid="hero-next" onClick={() => go(1)} className="w-11 h-11 rounded-full border border-white/30 text-white flex items-center justify-center hover:bg-[#E8842C] hover:border-[#E8842C] transition-colors">
            {isRtl ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0 h-1 bg-white/10">
        <motion.div key={idx} className="h-full bg-[#E8842C]" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: DURATION / 1000, ease: "linear" }} />
      </div>
    </section>
  );
}
